/**
 * TagInput — input do tworzenia i zarządzania tagami (F12.B T12.B.2).
 * Adaptacja z SellCall.
 */

import type { KeyboardEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  allowCustom?: boolean;
  suggestions?: string[];
}

export function TagInput({
  value = [],
  onChange,
  placeholder,
  allowCustom = true,
  suggestions = [],
}: TagInputProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const filteredSuggestions = suggestions.filter(
    (s) => s.toLowerCase().includes(inputValue.toLowerCase()) && !value.includes(s),
  );

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
      setInputValue('');
      setShowSuggestions(false);
    }
  };

  const removeTag = (tagToRemove: string) => {
    onChange(value.filter((t) => t !== tagToRemove));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      if (allowCustom || suggestions.includes(inputValue.trim())) {
        addTag(inputValue);
      }
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeTag(value[value.length - 1]!);
    }
  };

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-medium text-accent-700"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="hover:text-accent-900 focus:outline-none"
                aria-label={`Usuń tag ${tag}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setShowSuggestions(e.target.value.length > 0 && filteredSuggestions.length > 0);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() =>
            setShowSuggestions(inputValue.length > 0 && filteredSuggestions.length > 0)
          }
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder={
            placeholder ??
            t('tenant.modules.fields.tags.placeholder', { defaultValue: 'Dodaj tag...' })
          }
          className="w-full rounded-md border border-border-primary bg-bg-primary px-3 py-1.5 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-500 focus:ring-1 focus:ring-accent-500 focus:outline-none"
        />
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="absolute z-10 mt-1 w-full rounded-md border border-border-primary bg-bg-primary shadow-lg max-h-40 overflow-y-auto">
            {filteredSuggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => addTag(s)}
                className="block w-full px-3 py-1.5 text-left text-sm text-text-primary hover:bg-bg-tertiary"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
      <p className="text-xs text-text-muted">
        {allowCustom
          ? t('tenant.modules.fields.tags.hint', { defaultValue: 'Enter aby dodać tag' })
          : t('tenant.modules.fields.tags.hintSuggestions', {
              defaultValue: 'Enter aby dodać (tylko z sugestii)',
            })}
      </p>
    </div>
  );
}
